import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { COMFORT, ER_STANDARD, PLUS, SP, STANDARD, STD, WC_COMP } from 'constants/classOfService';
import { bookingShape } from 'constants/propTypes';
import { MISSED_DEPARTURE } from 'constants/afterSalesShortcuts';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import CanPerform from 'components/shared/CanPerform';
import MissedDepartureMutation from 'components/mutations/MissedDepartureMutation';
import SubsetOfPassengersInPalsBookingWarning from '../../AfterSalesWarning/SubsetOfPassengersInPalsBookingWarning/SubsetOfPassengersInPalsBookingWarning';

import {
  allSegmentsOfService,
  allSegmentsOfType,
  allSelectedProductsConfirmed,
  bookingIsConfirmed,
  canceledPassengersInSegments,
  oneSegmentSelected,
  passengersWithMixedJourneysInSegments,
  runAfterSaleValidator,
  segmentIsInMissedDeparturePeriod,
  selectedItemsHaveLuggageExtras,
  getFaresForPassengersInMixedJourney,
  selectedProductsHaveDifferentProductFamily,
  getProductFamiliesPerPassengersInMixedPF
} from '../AfterSalesCtas.utils';
import { CtaButton } from '../AfterSalesCtas.styles';
import WarningCta from '../WarningCta/WarningCta';
import ChildrenWillTravelAloneWarning from '../../AfterSalesWarning/ChildrenWillTravelAloneWarning/ChildrenWillTravelAloneWarning';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';

const MissedDepartureCta = ({
  selectedItemRefs,
  booking,
  history,
  label,
  disabled,
  setNoTrainAvailable,
  willResultInChildAlone = false,
  exchangingSubsetOfPaxsInPALBooking = false
}) => {
  const [blockAftersalesAsChildrenWillTravelAlone, setBlockAftersalesAsChildrenWillTravelAlone] = useState(false);
  const [warningSubsetOfPaxsInPals, setWarningSubsetOfPaxsInPals] = useState(false);
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);

  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  const isDisabled = (loading) =>
    loading ||
    disabled ||
    !runAfterSaleValidator(
      booking,
      selectedItemRefs
    )([
      bookingIsConfirmed,
      oneSegmentSelected,
      allSelectedProductsConfirmed,
      segmentIsInMissedDeparturePeriod,
      allSegmentsOfService([serviceTypeCodes.EUROSTAR]),
      allSegmentsOfType([STD, ER_STANDARD, STANDARD, SP, COMFORT, PLUS, WC_COMP])
    ]);

  const hasPassengersWithMixedJourneys = runAfterSaleValidator(
    booking,
    selectedItemRefs,
    null,
    'some'
  )([canceledPassengersInSegments, passengersWithMixedJourneysInSegments]);

  const faresForPassengersInMixedJourney = getFaresForPassengersInMixedJourney({
    booking,
    selectedItemRefs,
    hasPassengersWithMixedJourneys
  });

  const hasMoreThanOneProductFamily = runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsHaveDifferentProductFamily]);

  const productFamiliesPerPassengersInMixedPF = getProductFamiliesPerPassengersInMixedPF({
    booking,
    selectedItemRefs,
    hasMoreThanOneProductFamily
  });

  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');

  const onCtaClick = (missedDepartureMutation) => {
    if (willResultInChildAlone) {
      return setBlockAftersalesAsChildrenWillTravelAlone(true);
    }

    if (exchangingSubsetOfPaxsInPALBooking) {
      return setWarningSubsetOfPaxsInPals(true);
    }

    if (hasLuggageExtras) {
      setShowBlockTicketsWithExtraLuggageWarning(true);
      return;
    }

    setNoTrainAvailable(false);

    return missedDepartureMutation({
      variables: {
        reference: booking.reference,
        exchangeItems: selectedItemRefs.map((itemRef) => Number(itemRef)),
        shouldAllowNextDay: true
      }
    });
  };

  const onSubsetPaxCtaClick = (missedDepartureMutation) => {
    setNoTrainAvailable(false);

    return missedDepartureMutation({
      variables: {
        reference: booking.reference,
        exchangeItems: selectedItemRefs.map((itemRef) => Number(itemRef)),
        shouldAllowNextDay: true
      }
    });
  };

  if (blockAftersalesAsChildrenWillTravelAlone) {
    return <ChildrenWillTravelAloneWarning isOpen={true} closeModal={() => setBlockAftersalesAsChildrenWillTravelAlone(false)} />;
  }

  const toggleBlockTicketsWithExtraLuggageWarning = () => {
    setShowBlockTicketsWithExtraLuggageWarning(!showBlockTicketsWithExtraLuggageWarning);
  };

  return (
    <>
      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={toggleBlockTicketsWithExtraLuggageWarning}
      />

      <CanPerform afterSalesShortcut={MISSED_DEPARTURE}>
        {(isEligible) =>
          isEligible && (
            <MissedDepartureMutation
              onCompleted={({ missedDeparture }) => {
                if (missedDeparture?.updatedBooking) {
                  history.push(
                    `/exchange/${booking.reference}/confirm?isMissedDeparture=true${
                      membershipNumber ? `&membershipNumber=${membershipNumber}` : ''
                    }`
                  );
                } else if (missedDeparture?.nextAvailableTrain) {
                  history.push(
                    `/exchange/${booking.reference}/missed-departure/pax?bookingItems=${selectedItemRefs.join(
                      ','
                    )}&recommendedSeats=${missedDeparture?.nextAvailableTrain?.recommendedSeats
                      .map((seat) => `${seat.coach}:${seat.number}`)
                      .join(',')}&nextAvailableTrain=${missedDeparture?.nextAvailableTrain?.trainNumber}&serviceIdentifier=${
                      missedDeparture?.nextAvailableTrain?.serviceIdentifier
                    }${membershipNumber ? `&membershipNumber=${membershipNumber}` : ''}`
                  );
                } else {
                  window.scrollTo(0, 0);
                  setNoTrainAvailable(true);
                }
              }}
            >
              {(missedDepartureMutation, { loading }) => (
                <div>
                  {(hasPassengersWithMixedJourneys || hasMoreThanOneProductFamily) && !isDisabled(loading) ? (
                    <WarningCta
                      label={label}
                      title={<FormattedMessage id="afterSales.warning" />}
                      subtitle={<FormattedMessage id="afterSales.warningSelectionMessage" />}
                      hasPassengersWithMixedJourneys={hasPassengersWithMixedJourneys}
                      faresForPassengersInMixedJourney={faresForPassengersInMixedJourney}
                      hasMoreThanOneProductFamily={hasMoreThanOneProductFamily}
                      productFamiliesPerPassengersInMixedPF={productFamiliesPerPassengersInMixedPF}
                    />
                  ) : warningSubsetOfPaxsInPals ? (
                    <SubsetOfPassengersInPalsBookingWarning
                      isOpen={true}
                      closeModal={() => setWarningSubsetOfPaxsInPals(false)}
                      handleClick={() => onSubsetPaxCtaClick(missedDepartureMutation)}
                      loading={loading}
                      isDisabled={isDisabled(loading)}
                    />
                  ) : (
                    <CtaButton
                      data-cy="missedDeparture-cta"
                      onClick={() => onCtaClick(missedDepartureMutation)}
                      label={label}
                      loading={loading}
                      disabled={isDisabled(loading)}
                    />
                  )}
                </div>
              )}
            </MissedDepartureMutation>
          )
        }
      </CanPerform>
    </>
  );
};

MissedDepartureCta.propTypes = {
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  booking: bookingShape,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string
    })
  }),
  label: PropTypes.node,
  disabled: PropTypes.bool,
  setNoTrainAvailable: PropTypes.func.isRequired,
  willResultInChildAlone: PropTypes.bool,
  exchangingSubsetOfPaxsInPALBooking: PropTypes.bool
};

export default MissedDepartureCta;
